import * as React from 'react';
import L, {LatLng, latLng, LatLngBounds, LayersControlEvent} from 'leaflet'; // XXX Leaflet must be imported like this to allow direct access to L.drawLocal properties
import {Map, TileLayer, WMSTileLayer, LayersControl} from 'react-leaflet';
import {connect} from 'react-redux';
import {LEAFLET_EPSG25833_GEODATA, LEAFLET_EPSG25833_NIB} from '../../../map/NVDBCRS';
import MapUtils from '../../../map/map-utils';
import ExistingFeatures from './map/ExistingFeatures';
import ZoomingFeatureGroup from './map/ZoomingFeatureGroup';
import ExistingFeaturesButton from './map/ExistingFeaturesButton';
import ExitGeometryEditorButton from './map/ExitGeometryEditorButton';
import GeometryEditModal from './GeometryEditModal';
import DragSelect from './map/DragSelect';
import FeatureLayer from './map/FeatureLayer';
import {
    BoundingBox, DisplayFeature, DisplayFeatureType, GeometryType, UserSession
} from '../domain';
import {
    toggleFeatureSelected,
    toggleShowExistingFeatures,
    exitGeometryEditor,
    setSelectFeatures,
    editFeatureGeometry,
    showExistingFeatures,
    fetchDatacatalog,
    cutFeature,
    reloadExistingFeatures,
    getRoadSegments,
    GetRoadSegments,
    setCreateObjectWkt,
    setCreateObjectVisible,
    SetCreateObjectWkt,
    EditFeatureGeometry,
} from '../../../actions/actions';

import WKTParser from 'terraformer-wkt-parser';
import FeaturesState from '../domain/FeaturesState';
import datacatalog from '../../../../integration/datacatalog/datacatalog-services';
import AutoZoomButton from './map/AutoZoomButton';
import CutModeButton from './map/CutModeButton';
import {Geometry} from 'geojson';
import CutConfirmModal from './map/CutConfirmModal';
import CutErrorModal from './map/CutErrorModal';
import GrayScaleButton from './map/GrayScaleButton';
import classNames from 'classnames';
import {FeatureTypeSummary} from '../../../../integration/representation/feature-type';
import {RoadSegmentLayer} from './map/RoadSegmentLayer';
import RoadSegmentButton from './map/RoadSegmentButton';

const {BaseLayer} = LayersControl;

interface FeatureMapStateProps {
    isShowingExistingFeatures: boolean;
    editingGeometryFeatureId: string;
    selectedFeatureType: DisplayFeatureType;
    additionalSelectedFeatureTypes: DisplayFeatureType[];
    selectedFeatures: string[];
    showOnlySelected: boolean;
    featureTypes: FeatureTypeSummary[];
    user: UserSession;
    datacatalog: datacatalog;
    creatingObjectGeometry: GeometryType;
    features: DisplayFeature[];
}

interface FeatureMapDispatchProps {
    updateExistingFeatures: (bbox: BoundingBox) => void;
    onExistingObjectsClick: (bbox: BoundingBox, closeEnough: boolean) => void;
    onSelectFeatures: Function;
    exitGeometryEditorClick: (bbox: BoundingBox) => void;
    editFeatureGeometry: EditFeatureGeometry;
    onCutFeatureGeometry: (feature: DisplayFeature, position: L.LatLng) => void;
    onFeatureClick: (feature: DisplayFeature) => void;
    fetchDatacatalog: () => void;
    reloadExistingFeatures: (boundingBox: BoundingBox) => void;
    doGetRoadSegments: GetRoadSegments;
    setCreateObjectWkt: SetCreateObjectWkt;
    showCreateObjectModal: () => void;
}

type FeatureMapProps = FeatureMapStateProps & FeatureMapDispatchProps;

interface FeatureMapState {
    defaultLat: number;
    defaultLng: number;
    zoom: number;
    displayGeometryEditConfirm: boolean;
    geometryEditWkt: string;
    autoZoomEnabled: boolean;
    grayscaleEnabled: boolean;
    cutModeActive: boolean;
    dragSelectActive: boolean;
    cutData: CutData;
    cutErrors: string[];
    roadSegmentsEnabled: boolean;
    closeEnoughToShowFeaturesAndRoadSegments: boolean;
    crs: L.Proj.CRS;
    selectedFeatureTypeShouldBeIgnored: boolean;
}

/** Data for cut in progress. */
interface CutData {
    feature: DisplayFeature;
    position: L.LatLng;
}

class FeatureMapComponent extends React.Component<FeatureMapProps, FeatureMapState> {
    private drawControl: L.Control.Draw;
    private lengthMeasureControl: L.Control.PolylineMeasure;
    private gpsControl: any;
    private iconStyle: L.DivIcon;
    private zoomStartBounds: L.LatLngBounds;
    private mapRef: Map;

    constructor(props: FeatureMapProps) {
        super(props);
        this.state = {
            defaultLat: 63.43,
            defaultLng: 10.40,
            zoom: 4,
            displayGeometryEditConfirm: false,
            geometryEditWkt: null,
            autoZoomEnabled: true,
            grayscaleEnabled: false,
            cutModeActive: false,
            cutData: null,
            cutErrors: null,
            dragSelectActive: false,
            roadSegmentsEnabled: false,
            closeEnoughToShowFeaturesAndRoadSegments: false,
            crs: LEAFLET_EPSG25833_GEODATA,
            selectedFeatureTypeShouldBeIgnored: false,
        };
        this.iconStyle = new L.DivIcon({
            iconSize: new L.Point(5, 5)
        });
        L.drawLocal.draw.toolbar.buttons.polygon = 'Tegn flategeometri';
        L.drawLocal.draw.toolbar.buttons.polyline = 'Tegn linjegeometri';
        L.drawLocal.draw.toolbar.buttons.marker = 'Tegn punktgeometri';
        this.drawControl = new L.Control.Draw({
            draw: {
                polygon: {
                    icon: this.iconStyle
                },
                polyline: {
                    icon: this.iconStyle
                },
                point: {
                    icon: this.iconStyle
                },
                circle: false,
                rectangle: false
            }
        } as any);
        this.lengthMeasureControl = L.control.polylineMeasure({
            measureControlTitleOn: 'Mål avstand',
            measureControlTitleOff: 'Mål avstand'
        });
        this.gpsControl = new (L.Control as any).Gps({
            title: 'Vis posisjon i kartet',
            textErr: 'Posisjon er ikke tilgjengelig'
        });
        this.setMapRef = this.setMapRef.bind(this);
    }

    componentDidUpdate(prevProps: Readonly<FeatureMapProps>, prevState: Readonly<FeatureMapState>, snapshot?: any) {
        if (this.mapRef && prevProps.creatingObjectGeometry === null && this.props.creatingObjectGeometry !== null) {
            const shape = this.geometryTypeToShape(this.props.creatingObjectGeometry);
            switch (shape) {
                case 'marker':
                    new L.Draw.Marker(this.mapRef.leafletElement as L.DrawMap, {icon: this.iconStyle}).enable();
                    break;
                case 'polyline':
                    new L.Draw.Polyline(this.mapRef.leafletElement as L.DrawMap, {icon: this.iconStyle}).enable();
                    break;
                case 'polygon':
                    new L.Draw.Polygon(this.mapRef.leafletElement as L.DrawMap, {icon: this.iconStyle}).enable();
                    break;
            }
        }

        // We only ignore selected feature for one render after changing map base layer
        if (this.props.selectedFeatureType && this.state.selectedFeatureTypeShouldBeIgnored) {
            this.setState({
                selectedFeatureTypeShouldBeIgnored: false
            })
        }
    }

    handleMoveStart() {
        this.zoomStartBounds = this.mapRef.leafletElement.getBounds();
    }

    handleMoveEnd(e: L.LeafletEvent) {
        if (this.props.isShowingExistingFeatures && this.state.closeEnoughToShowFeaturesAndRoadSegments) {
            this.props.updateExistingFeatures(this.getBoundingBox());
        }

        if (this.state.closeEnoughToShowFeaturesAndRoadSegments) {
            const bounds = e.target.getBounds() as LatLngBounds;
            const southWest = bounds.getSouthWest();
            const northEast = bounds.getNorthEast();
            const southWestPositionJson = MapUtils.wgs84ToUtm33(southWest);
            const northEastPositionJson = MapUtils.wgs84ToUtm33(northEast);
            const southWestX = southWestPositionJson.easting;
            const southWestY = southWestPositionJson.northing;
            const northEastX = northEastPositionJson.easting;
            const northEastY = northEastPositionJson.northing;
            this.props.doGetRoadSegments(southWestX, southWestY, northEastX, northEastY);
        }
    }

    handleZoomEnd(e: L.LeafletEvent) {
        const zoom: number = e.target.getZoom();
        this.setState({
            zoom,
            closeEnoughToShowFeaturesAndRoadSegments: zoom >= 12
        });
        this.handleMoveEnd(e);
    }

    toggleAutoZoom() {
        this.setState({
            autoZoomEnabled: !this.state.autoZoomEnabled
        });
    }

    toggleShowRoadSegments() {
        this.setState({
            roadSegmentsEnabled: !this.state.roadSegmentsEnabled
        });
    }

    toggleGrayScale() {
        this.setState({
            grayscaleEnabled: !this.state.grayscaleEnabled
        });
    }

    toggleCutMode() {
        this.setState({
            cutModeActive: !this.state.cutModeActive
        });
    }

    getBoundingBox = () => {
        const bounds = this.mapRef.leafletElement.getBounds();
        return new BoundingBox(
            bounds.getNorthEast(),
            bounds.getSouthWest(),
            '4326'
        );
    }

    baseLayerChanged = (e: LayersControlEvent) => {
        this.setBaseLayerCrs(e);
        this.reloadExistingFeatures();

        // Trigger componentDidUpdate
        this.setState({
            autoZoomEnabled: this.state.autoZoomEnabled,
            dragSelectActive: false
        })

        // Ignore selected feature type on first render after map base layer change to maintain button order
        if (this.props.selectedFeatureType) {
            this.setState({
                selectedFeatureTypeShouldBeIgnored: true
            })
        }
    }

    setBaseLayerCrs = (e: LayersControlEvent) => {
        const newLayerName = e.name;
        if (newLayerName === "Norge i Bilder") {
            this.setState({
                crs: LEAFLET_EPSG25833_NIB
            })
        }
        if (newLayerName === "Geodata Online") {
            this.setState({
                crs: LEAFLET_EPSG25833_GEODATA
            })
        }
    }

    reloadExistingFeatures = () => {
        if (!this.props.isShowingExistingFeatures) {// Not showing, so should not be reloaded
            return;
        }
        if (this.state.closeEnoughToShowFeaturesAndRoadSegments) {
            this.props.reloadExistingFeatures(this.getBoundingBox());
        }
    }

    onDragSelected = (active: boolean) => {
        if (active) {
            this.setState({
                dragSelectActive: true,
                cutModeActive: false
            });
        } else {
            this.setState({
                dragSelectActive: false
            });
        }
    }

    getCenter = (): LatLng => {
        if (this.mapRef) {
            return this.mapRef.leafletElement.getCenter();
        } else {
            return latLng(this.state.defaultLat, this.state.defaultLng);
        }
    }

    setMapRef(element: Map) {
        this.mapRef = element;
    }

    render() {
        this.handleLeafletDraw();
        this.initLengthMeasurer();
        this.initGps();
        const mapClasses = classNames({
            'map': true,
            'full-height': true,
            'grayscale': this.state.grayscaleEnabled
        });
        if (this.state.crs === LEAFLET_EPSG25833_GEODATA) {
            return (
                <Map
                    className={mapClasses}
                    crs={this.state.crs}
                    center={this.getCenter()}
                    zoom={this.state.zoom}
                    onbaselayerchange={e => this.baseLayerChanged(e)}
                    onmovestart={() => this.handleMoveStart()}
                    ondragend={(e) => this.handleMoveEnd(e)}
                    onresize={(e) => this.handleMoveEnd(e)}
                    onzoomend={(e) => this.handleZoomEnd(e)}
                    doubleClickZoom={'center'}
                    ref={this.setMapRef}>

                    <GrayScaleButton toggleGrayScale={() => this.toggleGrayScale()} grayscaleEnabled={this.state.grayscaleEnabled} position="bottomleft"/>

                    <LayersControl position="bottomleft">
                        <BaseLayer checked name="Geodata Online">
                            <TileLayer
                                maxZoom={20}
                                minZoom={3}
                                maxNativeZoom={16}
                                attribution="NVDB"
                                url="https://nvdbcache.geodataonline.no/arcgis/rest/services/Trafikkportalen/GeocacheTrafikkJPG/MapServer/tile/{z}/{y}/{x}"
                            />
                        </BaseLayer>
                        <BaseLayer name="Norkart WebAtlas">
                            <WMSTileLayer
                                maxZoom={20}
                                minZoom={0}
                                layers="WEBATLASNorge:Norge"
                                format="image/png"
                                url={'/rest/map/webatlas'}
                                attribution="Webatlas"
                            />
                        </BaseLayer>
                        <BaseLayer name="Norge i Bilder">
                            <TileLayer
                                maxZoom={17}
                                minZoom={0}
                                layers="ortofoto"
                                format="image/jpeg"
                                url={'https://opencache3.statkart.no/gatekeeper/gk/gk.open_nib_utm33_wmts_v2?layer=Nibcache_UTM33_EUREF89_v2&style=default&tilematrixset=default028mm&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fjpeg&TileMatrix={z}&TileCol={x}&TileRow={y}'}
                                attribution="GeoNorge"
                            />
                        </BaseLayer>
                    </LayersControl>

                    {this.getAdditionalMapLayersAndButtons()}
                </Map>
            );
        } else if (this.state.crs === LEAFLET_EPSG25833_NIB) {
            return (
                <>
                    <p style={{height: 0, margin: 0}}>easter egg</p>
                    <Map
                        className={mapClasses}
                        crs={this.state.crs}
                        center={this.getCenter()}
                        zoom={this.state.zoom}
                        onbaselayerchange={e => this.baseLayerChanged(e)}
                        onmovestart={() => this.handleMoveStart()}
                        ondragend={(e) => this.handleMoveEnd(e)}
                        onresize={(e) => this.handleMoveEnd(e)}
                        onzoomend={(e) => this.handleZoomEnd(e)}
                        doubleClickZoom={'center'}
                        ref={this.setMapRef}>

                        <GrayScaleButton
                            toggleGrayScale={() => this.toggleGrayScale()}
                            grayscaleEnabled={this.state.grayscaleEnabled}
                            position="bottomleft"
                        />

                        <LayersControl position="bottomleft">
                            <BaseLayer name="Geodata Online">
                                <TileLayer
                                    maxZoom={20}
                                    minZoom={3}
                                    maxNativeZoom={16}
                                    attribution="NVDB"
                                    url="https://nvdbcache.geodataonline.no/arcgis/rest/services/Trafikkportalen/GeocacheTrafikkJPG/MapServer/tile/{z}/{y}/{x}"
                                />
                            </BaseLayer>
                            <BaseLayer name="Norkart WebAtlas">
                                <WMSTileLayer
                                    maxZoom={20}
                                    minZoom={0}
                                    layers="WEBATLASNorge:Norge"
                                    format="image/png"
                                    url={'/rest/map/webatlas'}
                                    attribution="Webatlas"
                                />
                            </BaseLayer>
                            <BaseLayer checked name="Norge i Bilder">
                                <TileLayer
                                    maxZoom={17}
                                    minZoom={0}
                                    layers="ortofoto"
                                    format="image/jpeg"
                                    url={'https://opencache3.statkart.no/gatekeeper/gk/gk.open_nib_utm33_wmts_v2?layer=Nibcache_UTM33_EUREF89_v2&style=default&tilematrixset=default028mm&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fjpeg&TileMatrix={z}&TileCol={x}&TileRow={y}'}
                                    attribution="GeoNorge"
                                />
                            </BaseLayer>
                        </LayersControl>

                        {this.getAdditionalMapLayersAndButtons()}
                    </Map>
                </>
            );
        }
    }

    getAdditionalMapLayersAndButtons() {
        return <>
            {this.state.roadSegmentsEnabled && this.state.closeEnoughToShowFeaturesAndRoadSegments &&
                <RoadSegmentLayer/>}

            {this.props.editingGeometryFeatureId &&
                <ExitGeometryEditorButton exitGeometryEditor={this.props.exitGeometryEditorClick}/>}

            <AutoZoomButton
                toggleAutoZoom={() => this.toggleAutoZoom()}
                autoZoomEnabled={this.state.autoZoomEnabled}
            />

            <RoadSegmentButton
                toggleShowRoadSegments={() => this.toggleShowRoadSegments()}
                showRoadSegments={this.state.roadSegmentsEnabled}
            />

            <CutModeButton
                toggleCutMode={() => this.toggleCutMode()}
                cutModeEnabled={!this.state.dragSelectActive}
                cutModeActive={this.state.cutModeActive}
            />

            {this.props.selectedFeatureType && !this.state.selectedFeatureTypeShouldBeIgnored &&
            <DragSelect
                position="topleft"
                onDragSelected={this.onDragSelected}
                onSelectFeatures={this.props.onSelectFeatures}
                selectedFeatureType={this.props.selectedFeatureType}
            />}


            {this.props.selectedFeatureType && !this.state.selectedFeatureTypeShouldBeIgnored &&
            <ExistingFeaturesButton
                shouldEnable={this.state.closeEnoughToShowFeaturesAndRoadSegments}
                toggleShowExistingFeatures={(boundingbox: BoundingBox) =>
                    this.props.onExistingObjectsClick(boundingbox, this.state.closeEnoughToShowFeaturesAndRoadSegments)}
            />}

            {this.getMarkersAndSuch()}
            {this.getExistingFeatures()}

            {this.state.displayGeometryEditConfirm &&
            <GeometryEditModal
                displayGeometryEditConfirm={this.state.displayGeometryEditConfirm}
                setDefaultState={this.resetEditGeometryState}
                editFeatureGeometry={this.props.editFeatureGeometry}
                geometryEditWkt={this.state.geometryEditWkt}
                editingGeometryFeatureId={this.props.editingGeometryFeatureId}
                leafletElement={this.mapRef.leafletElement}
                parent={this}
            />}

            {this.state.cutData &&
            <CutConfirmModal
                feature={this.state.cutData.feature}
                position={this.state.cutData.position}
                confirmCut={this.confirmCut}
                cancelCut={this.resetCutState}
            />}

            {this.state.cutErrors &&
            <CutErrorModal
                errors={this.state.cutErrors}
                close={this.resetCutState}
            />}
        </>
    }

    resetEditGeometryState(that: FeatureMapComponent) {
        that.setState({
            displayGeometryEditConfirm: false,
            geometryEditWkt: null
        });
    }

    handleLeafletDraw() {
        let types: GeometryType[] = [];
        if (this.props.editingGeometryFeatureId) {
            types = this.props.selectedFeatureType.geometryTypes;
        } else if(this.props.creatingObjectGeometry){
            types = [this.props.creatingObjectGeometry];
        }
        if (types.length > 0 && this.mapRef) {
            if (!(this.drawControl as any)._map) {
                const validTypes = types.map((type) => {
                    return this.geometryTypeToShape(type);
                });
                const options: ('polygon' | 'polyline' | 'marker')[] = ['polygon', 'polyline', 'marker'];
                // XXX Really a L.Control.DrawOptions (see L.Control.Draw below), but hard to type the way this code is structured
                const drawShapes: any = {
                    circle: null,
                    rectangle: null
                };
                options.forEach((type) => {
                    if (validTypes.includes(type)) {
                        drawShapes[type] = {
                            icon: this.iconStyle
                        };
                    } else {
                        drawShapes[type] = false;
                    }
                });
                this.drawControl = new L.Control.Draw({
                    draw: drawShapes
                });

                this.mapRef.leafletElement.addControl(this.drawControl);
                this.mapRef.leafletElement.on('draw:created', (e) => {
                    this.handleGeometryEdit(e as L.DrawEvents.Created);
                });
            }
        } else {
            if ((this.drawControl as any)._map) {
                this.mapRef.leafletElement.removeControl(this.drawControl);
                this.mapRef.leafletElement.off('draw:created');
            }
        }
    }

    initLengthMeasurer() {
        if (this.mapRef && !(this.lengthMeasureControl as any)._map) {
            this.mapRef.leafletElement.addControl(this.lengthMeasureControl);
        }
    }

    initGps() {
        if (this.mapRef && !(this.gpsControl as any)._map) {
            this.mapRef.leafletElement.addControl(this.gpsControl);
        }
    }

    geometryTypeToShape(geometryType: string): 'polyline' | 'polygon' | 'marker' {
        switch (geometryType) {
            case 'LINE':
                return 'polyline';
            case 'POLYGON':
                return 'polygon';
            case 'POINT':
                return 'marker';
        }
        throw new Error('Error, unrecognized geometry type');
    }

    handleGeometryEdit(event: L.DrawEvents.Created) {
        const coordsAndShape = this.coordsAndShape(event);
        const wkt = WKTParser.convert(coordsAndShape);

        if (this.props.creatingObjectGeometry) {
            this.props.setCreateObjectWkt(wkt);
            this.props.showCreateObjectModal();
        } else {
            this.setState({
                displayGeometryEditConfirm: true,
                geometryEditWkt: wkt
            });
        }
    }

    coordsAndShape(event: L.DrawEvents.Created): Geometry {
        let coords;
        switch (event.layerType) {
            case 'polygon':
                coords = ((event.layer as any)._latlngs[0] as L.LatLng[]).map(point => {
                    return [point.lat, point.lng, 0];
                });
                // Første og siste koordinatet må være samme i polygon
                coords.push(coords[0]);
                return {coordinates: [coords], type: 'Polygon'};
            case 'polyline':
                coords = ((event.layer as L.Polyline).getLatLngs() as L.LatLng[]).map(point => {
                    return [point.lat, point.lng, 0];
                });
                return {coordinates: coords, type: 'LineString'};
            case 'marker':
                coords = [(event.layer as L.Marker).getLatLng().lat, (event.layer as L.Marker).getLatLng().lng, 0];
                return {coordinates: coords, type: 'Point'};
        }
    }

    getMarkersAndSuch() {
        const featureList = this.props.features ?? [];
        const additionalFeaturesList = this.props.additionalSelectedFeatureTypes.map(type => type.features)
            .reduce((prev, curr) => prev.concat(curr), []);

        if (this.props.selectedFeatureType) {
            const features = featureList
                .filter(feature => !this.props.showOnlySelected || this.props.selectedFeatures.includes(feature.id))
                .map(feature => this.featureToLayer(feature, true));
            const additionalFeatures = this.props.showOnlySelected ? [] : additionalFeaturesList
                .map(feature => this.featureToLayer(feature, false));

            return <ZoomingFeatureGroup
                selectedFeatureTypeId={this.props.selectedFeatureType.typeId}
                updateOnFeatureChange={true}
                enableAutoZoom={this.state.autoZoomEnabled}
            >
                {features}
                {additionalFeatures}
            </ZoomingFeatureGroup>;
        } else {
            return null;
        }
    }

    featureToLayer(feature: DisplayFeature, clickable: boolean) {
        const wkt = feature.geometry.wkt;
        if (wkt) {
            const featurePosUtm33 = WKTParser.parse(wkt);
            const featurePosition = MapUtils.convertUtm33ToWgs84(featurePosUtm33);
            const isSelected = this.props.selectedFeatures.indexOf(feature.id) !== -1;
            return <FeatureLayer
                isSelected={isSelected}
                cutMode={this.state.cutModeActive}
                featureClicked={(latLng: L.LatLng) => this.featureClicked(feature, latLng, clickable)}
                key={feature.id}
                data={featurePosition}
                feature={feature} />;
        } else {
            return null;
        }
    }

    featureClicked(feature: DisplayFeature, latLng: L.LatLng, clickable: boolean) {
        if (clickable) {
            if (this.state.cutModeActive) {
                const errors: string[] = [];
                const geometryType = MapUtils.getGeometryTypeFromWkt(feature.geometry.wkt);
                if (geometryType === 'POINT') {
                    errors.push('Kan ikke klippe punkt');
                } else if (geometryType === 'POLYGON') {
                    errors.push('Klipping av polygon er ikke støttet');
                }

                if (feature.operation !== 'CREATE') {
                    errors.push('Kan kun klippe vegobjekt som er ny i kontrakt');
                }
                if (errors.length === 0) {
                    this.setState({
                        cutData: {feature, position: latLng}
                    });
                } else {
                    this.setState({
                        cutErrors: errors
                    });
                }
            } else {
                this.props.onFeatureClick(feature);
            }
        }
    }

    getExistingFeatures() {
        if (this.props.isShowingExistingFeatures) {
            return <ExistingFeatures/>;
        } else {
            return null;
        }
    }

    private confirmCut = (feature: DisplayFeature, position: L.LatLng): void => {
        this.props.onCutFeatureGeometry(feature, position);
        this.setState({
            cutData: null,
            cutModeActive: false
        });
    }

    private resetCutState = () => {
        this.setState({
            cutData: null,
            cutErrors: null
        });
    }
}

const mapStateToProps = (state: FeaturesState): FeatureMapStateProps => {
    return {
        features: state.features,
        editingGeometryFeatureId: state.editingGeometryFeatureId,
        selectedFeatureType: state.selectedFeatureType,
        additionalSelectedFeatureTypes: state.additionalSelectedFeatureTypes,
        selectedFeatures: state.selectedFeatures,
        showOnlySelected: state.showOnlySelectedFeatures,
        featureTypes: state.featureTypes,
        isShowingExistingFeatures: state.isShowingExistingFeatures,
        user: state.user,
        datacatalog: state.datacatalog,
        creatingObjectGeometry: state.creatingObjectGeometry,
    };
};

const mapDispatchToProps: FeatureMapDispatchProps = ({
    onFeatureClick: toggleFeatureSelected,
    onExistingObjectsClick: toggleShowExistingFeatures,
    exitGeometryEditorClick: exitGeometryEditor,
    updateExistingFeatures: showExistingFeatures,
    onSelectFeatures: setSelectFeatures,
    onCutFeatureGeometry: cutFeature,
    reloadExistingFeatures,
    editFeatureGeometry,
    fetchDatacatalog,
    doGetRoadSegments: getRoadSegments,
    setCreateObjectWkt,
    showCreateObjectModal: () => setCreateObjectVisible(true),
});

export default connect(mapStateToProps, mapDispatchToProps)(FeatureMapComponent);
